import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api, rupee } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [attrs, setAttrs] = useState({ category: [], audience: [], size: [], color: [], fabric: [] });
  const [price, setPrice] = useState([0, 1000]);
  const [loading, setLoading] = useState(true);

  const get = (k) => params.get(k) || "";
  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || p.get(k) === v) p.delete(k); else p.set(k, v);
    setParams(p);
  };

  useEffect(() => { api.get("/attributes").then((r) => setAttrs(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    ["category", "audience", "size", "color", "fabric", "search"].forEach((k) => {
      const v = params.get(k);
      if (v) q[k] = v;
    });
    q.min_price = price[0]; q.max_price = price[1];
    api.get("/products", { params: q }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [params, price]);

  useEffect(() => { load(); }, [load]);

  const Filters = () => (
    <div className="space-y-7">
      <FilterGroup title="Category" options={attrs.category} selected={get("category")} onSelect={(v) => setParam("category", v)} />
      <FilterGroup title="Shop For" options={attrs.audience} selected={get("audience")} onSelect={(v) => setParam("audience", v)} />
      <FilterGroup title="Size" options={attrs.size} selected={get("size")} onSelect={(v) => setParam("size", v)} pill />
      <FilterGroup title="Color" options={attrs.color} selected={get("color")} onSelect={(v) => setParam("color", v)} pill />
      <FilterGroup title="Fabric" options={attrs.fabric} selected={get("fabric")} onSelect={(v) => setParam("fabric", v)} />
      <div>
        <h4 className="text-xs uppercase tracking-widest font-semibold text-primary mb-4">Price</h4>
        <Slider min={0} max={1000} step={10} value={price} onValueChange={setPrice} data-testid="price-slider" />
        <div className="flex justify-between text-sm text-muted-foreground mt-2 font-mono">
          <span>{rupee(price[0])}</span><span>{rupee(price[1])}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight">Shop All</h1>
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-2">{products.length} styles {get("search") && `for “${get("search")}”`}</p>
        </div>
        <Sheet>
          <SheetTrigger asChild>
            <button className="lg:hidden inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-[0.15em]" data-testid="filters-toggle">
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[hsl(var(--ivory))] overflow-y-auto w-80 bc-scroll">
            <div className="mt-8"><Filters /></div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block"><Filters /></aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[hsl(var(--ivory))] animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24 text-muted-foreground font-light">No products match your filters.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-12">
              {products.map((p, i) => (
                <div key={p.id} data-aos="fade-up" data-aos-delay={(i % 3) * 120}><ProductCard p={p} /></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FilterGroup = ({ title, options, selected, onSelect, pill }) => (
  <div>
    <h4 className="text-xs uppercase tracking-widest font-semibold text-primary mb-3">{title}</h4>
    <div className={pill ? "flex flex-wrap gap-2" : "space-y-1.5"}>
      {options.map((o) =>
        pill ? (
          <button key={o} onClick={() => onSelect(o)} data-testid={`filter-${title}-${o}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selected === o ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
            {o}
          </button>
        ) : (
          <button key={o} onClick={() => onSelect(o)} data-testid={`filter-${title}-${o}`}
            className={`flex items-center gap-2 text-sm w-full text-left px-2 py-1.5 rounded-lg transition-colors ${selected === o ? "text-primary font-semibold bg-secondary" : "text-foreground/80 hover:bg-secondary"}`}>
            <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected === o ? "bg-primary border-primary" : "border-input"}`}>
              {selected === o && <X className="w-2.5 h-2.5 text-white" />}
            </span>
            {o}
          </button>
        )
      )}
    </div>
  </div>
);
